import clsx from 'clsx';
import React from 'react';
import { useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import insertScript from '@dashboard/lib/insertScript';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import { isProduction } from '@dashboard/services/env';
import isFreeSeller from '@dashboard/services/isFreeSeller';
import useTopBannerHeight from '@dashboard/hooks/useTopBannerHeight';
import { useIsLaptop } from '@dashboard/hooks/use-mobile';

import Meta from '@dashboard/components/Meta';
import Banner from '@dashboard/components/Banner';
import AddStoreNameDialog from '@dashboard/components/AddStoreNameDialog';
import UpgradeToYearlyDialog from '@dashboard/components/UpgradeToYearlyDialog';

import Navigation from './Navigation';
import MainNavRail, { RAIL_WIDTH } from './MainNavRail';
import AdminGhostingDialog from '@/dashboard/admin/components/AdminGhostingDialog';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { injectTrackingScript } from '@/dashboard/lib/scriptInjector';
import { forgetOpenGroups } from '@dashboard/scenes/PageEdit/components/EditorSidebar';
import { SidebarInset, SidebarOpenButton, SidebarProvider } from '@dashboard/components/ui/sidebar';

const MainLayout = ({ isFocused, children }) => {
  useTracking();
  const location = useLocation();
  const history = useHistory();
  const { user, seller, isAuth, isAdmin } = useAuth();
  const { adminGhostingSellerId } = useAdminGhosting();
  const bannerRef = useTopBannerHeight();
  const isLaptop = useIsLaptop();

  const [identifiedCrispUser, setIdentifiedCrispUser] = React.useState(false);
  const [identifiedCrispSeller, setIdentifiedCrispSeller] = React.useState(false);

  // track pageviews
  useEffect(() => {
    tracking.trackPageview();
  }, [location]);

  // load rewardful
  useEffect(() => {
    function loadRewardful() {
      injectTrackingScript(
        `
        <script>(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');</script>
        <script async src='https://r.wdfl.co/rw.js' data-rewardful='e2e9f1'></script>
      `,
      );
    }

    // Skip when PublicLayout already injected it (e.g. signup -> dashboard in one SPA session).
    if (isProduction && !window._rwq) {
      loadRewardful();
    }
  }, []);

  // load google ads
  useEffect(() => {
    function loadGoogleAds() {
      injectTrackingScript(
        `
        <!-- Google tag (gtag.js) -->
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-ZQ3SPC315S"></script>
        <script>
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-ZQ3SPC315S', {
            send_page_view: false,
          });

          gtag('config', 'AW-16833295745', {
            enhanced_conversions: true,
            allow_enhanced_conversions: true,
            send_page_view: false
          });
        </script>
      `,
      );
    }

    if (isProduction) {
      loadGoogleAds();
    }
  }, []);

  // load facebook pixel
  useEffect(() => {
    function loadFacebookPixel() {
      insertScript(`
        <!-- Meta Pixel Code -->
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '976736210989373');
        fbq('track', 'PageView');
        <!-- End Meta Pixel Code -->
      `);
    }

    if (isProduction) {
      loadFacebookPixel();
    }
  }, []);

  useEffect(() => {
    function loadLinkedInInsight() {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        _linkedin_partner_id = "7177180";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);

        (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
      `;
      document.body.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `
        <img
          height="1"
          width="1"
          style="display:none;"
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=7177180&fmt=gif"
        />
      `;
      document.body.appendChild(noscript);
    }

    if (isProduction) {
      loadLinkedInInsight();
    }
  }, []);

  useEffect(() => {
    if (isFreeSeller(seller)) return;

    function loadCrisp() {
      insertScript(`
        window.$crisp=[];
        window.CRISP_WEBSITE_ID="abe8e9e1-02b6-4715-b289-7573fd280b36";

        (function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
      `);
    }

    if (!window.$crisp) {
      loadCrisp();
    }
  }, [seller]);

  useEffect(() => {
    if (isFreeSeller(seller)) return;

    function identifyCrispUser() {
      if (identifiedCrispUser || !user?._id) {
        return;
      }

      if (window.$crisp) {
        insertScript(`(function() {
          window.$crisp.push(["set", "user:email", ["${user.email}"]]);
          window.$crisp.push(["set", "user:nickname", ["${user.name}"]]);
        })();`);

        setIdentifiedCrispUser(true);
      }
    }

    identifyCrispUser();
  }, [user, identifiedCrispUser, seller]);

  useEffect(() => {
    if (isFreeSeller(seller)) return;

    function identifyCrispSeller() {
      if (identifiedCrispSeller || !seller?._id) {
        return;
      }

      if (window.$crisp) {
        insertScript(`(function() {
          window.$crisp.push(["set", "session:data", [
            [
              ["seller_id", "${seller?._id}"],
              ["subscription_status", "${seller.subscriptionStatus}"],
              ["plan_handle", "${seller.plan?.handle}"],
            ]
          ]]);

          // track segments
          window.$crisp.push([
            "set",
            "session:segments",
            [
              [
                "${seller.subscriptionStatus}", // trialing, active, past_due etc
                "${seller.plan?.handle}", // launch, scale
              ],
            ],
            true // true to replace existing segments
          ]);
        })();`);

        setIdentifiedCrispSeller(true);
      }
    }

    identifyCrispSeller();
  }, [seller, identifiedCrispSeller]);

  const showAddStoreNameDialog = seller._id && !seller.displayName && !isAdmin;

  const isPageDetail =
    location.pathname.includes('/pages/') &&
    !location.pathname.includes('/checkouts') &&
    !location.pathname.includes('/forms');

  const isCouponNew = location.pathname.includes('/coupons/new');

  useEffect(() => {
    if (!isFocused) forgetOpenGroups();
  }, [isFocused]);

  // Create flows, Add booking and Invite staff are focused too but keep their own back button and no menu.
  // Tablets leave the rail out: the editor's back link and sidebar are enough there.
  const hasRail =
    isLaptop &&
    isFocused &&
    /^\/pages\/(?!new\b|create\b)[^/]+/.test(location.pathname) &&
    !/\/(add-booking|staff\/invite)$/.test(location.pathname);

  const sidebarStorageKey = isFocused ? 'editor-sidebar-open' : 'main-sidebar-open';

  const isFreeCapReached = isFreeSeller(seller) && seller?.freeCapReached;
  const isTrialCapReached = seller?.subscriptionStatus === 'trialing' && seller?.trialCapReached;

  return (
    <>
      {(isFreeCapReached || isTrialCapReached) && (
        <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-50">
          <Banner>
            <Banner.Message>
              {isTrialCapReached
                ? 'Trial revenue limit reached. Selling is paused until you upgrade.'
                : 'Free plan revenue limit reached. Selling is paused until you upgrade or your 30-day limit resets.'}
            </Banner.Message>
            <Banner.Action>
              <button
                type="button"
                onClick={() => history.push('/upgrade')}
                className="underline text-white"
              >
                Upgrade
              </button>
            </Banner.Action>
          </Banner>
        </div>
      )}

      {showAddStoreNameDialog && <AddStoreNameDialog isOpen onClose={() => {}} />}

      <UpgradeToYearlyDialog />

      <SidebarProvider
        key={sidebarStorageKey}
        // Laptops have no close button, so both sidebars stay open; tablets can close them.
        open={isLaptop || undefined}
        onOpenChange={isLaptop ? () => {} : undefined}
        storageKey={isLaptop ? undefined : sidebarStorageKey}
        style={hasRail ? { '--sidebar-offset': RAIL_WIDTH } : undefined}
        className="mx-auto max-w-[2560px]"
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
              body {
                overscroll-behavior-y: none;
                background: #fff;
              }
              .dark body {
                background: var(--background);
              }`,
          }}
        />

        <Meta
          noIndex
          meta={{
            description:
              'Checkout pages and forms to sell your products and services. Installed in minutes without coding. Sell products, subscriptions, digital downloads, featured listings, event tickets and more.',
            url: 'https://checkoutpage.com',
            image: 'https://checkoutpage.com/assets/media-card.png',
            title: 'Checkout Page',
          }}
        />

        {hasRail && <MainNavRail />}

        {!isFocused && (
          <Navigation
            isLogged={isAuth}
            user={user}
            hasTopBanner={isFreeCapReached || isTrialCapReached}
          />
        )}

        <SidebarInset
          className={clsx('bg-white dark:bg-background', {
            'mt-12 md:mt-0':
              !isPageDetail && !isCouponNew && !isFreeCapReached && !isTrialCapReached,
            'mt-[calc(3rem_+_var(--top-banner-h,0px))] md:mt-[var(--top-banner-h,0px)]':
              !isPageDetail && (isFreeCapReached || isTrialCapReached),
            'mt-[var(--top-banner-h,0px)]': isPageDetail && (isFreeCapReached || isTrialCapReached),
          })}
        >
          {/* Sits in the inset's top margin and scrolls away with the page, so it never covers content. */}
          {!isFocused && <SidebarOpenButton className="absolute -top-10 left-4 z-10 md:hidden" />}
          {children}
        </SidebarInset>
      </SidebarProvider>

      {isAdmin && adminGhostingSellerId && <AdminGhostingDialog sellerId={adminGhostingSellerId} />}
    </>
  );
};

export default MainLayout;
